/**
 * The shared player-filter body: six collapsible sections, no chrome.
 *
 * Extracted from `InlineFilterPanelView` by SE-5552. The desktop filter
 * surfaces — Watchlist's `FilterSheet`, Pipeline's `AddPlayersFilterPanel`
 * and the search modal's `InlineFilterPanel` — each render this same body
 * (chip taxonomies, role grid, range fields) inside different outer chrome,
 * and were converged onto the same molecules by SE-6867/SE-6871/SE-7144.
 * This is where that body now lives so the three stop being three copies.
 *
 * **Only `InlineFilterPanel` consumes it today.** `FilterSheet` and
 * `AddPlayersFilterPanel` still carry their own copies; adopting this is the
 * remaining half of SE-5552's unification. Until they do, this relocates the
 * duplication rather than removing it.
 *
 * Stateless: section expansion, chip-dropdown open state and every filter
 * value arrive as props from `useFilterPanelLogic`, which all three surfaces
 * already share.
 * @module components/organisms/shared/PlayerFilterSections/PlayerFilterSectionsView
 */

import React, { memo } from "react";

import {
  DEFAULT_AGE_RANGE,
  DEFAULT_CONTRACT_MONTHS_RANGE,
  DEFAULT_SCORE_RANGE,
  DEFAULT_VALUATION_RANGE,
} from "@/constants/filterDefaults";

import type {
  CoreRangeFilterProps,
  FilterOption,
  ReportStatsFilterProps,
  ScoreRangeFilterProps,
} from "@/types/filter";
import CustomMetricFilterFields from "@/features/scouting/components/organisms/CustomMetricFilterFields";
import FilterChip from "@/features/scouting/components/molecules/FilterChip";
import RangeFilterField from "@/components/organisms/shared/RangeFilterField";
import RoleChipGrid from "@/features/scouting/components/molecules/RoleChipGrid";
import SectionHeader from "@/features/scouting/components/molecules/SectionHeader";

import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

import formatNumberWithCommas from "@/utils/formatNumberWithCommas";
import LeaguePresetControls from "@/features/player-filter/components/LeaguePresetControls";
import type { LeaguePresetOption } from "@/features/player-filter/utils/leaguePresets";

/** Section key identifiers */
type SectionKey =
  | "basic"
  | "financial"
  | "fitAndRatings"
  | "performance"
  | "goalkeeper"
  | "reportStats";

/** Props for PlayerFilterSectionsView */
interface PlayerFilterSectionsViewProps
  extends CoreRangeFilterProps, ScoreRangeFilterProps, ReportStatsFilterProps {
  /* --- Section state --- */
  readonly sectionCounts: Readonly<Record<SectionKey, number>>;
  readonly expandedSections: Readonly<Record<SectionKey, boolean>>;
  readonly sectionToggleHandlers: Readonly<Record<SectionKey, () => void>>;

  /* --- Roles --- */
  readonly roleOptions: readonly FilterOption[];
  readonly selectedRoleArchetypes: readonly string[];
  readonly onToggleRole: (id: string) => void;

  /* --- Positions --- */
  readonly positionOptions: readonly FilterOption[];
  readonly selectedPositionIds: readonly string[];
  readonly onTogglePosition: (id: string) => void;
  readonly onClearPositions: () => void;
  readonly isPositionFilterOpen: boolean;
  readonly onTogglePositionFilter: () => void;
  readonly onClosePositionFilter: () => void;

  /* --- Clubs --- */
  readonly clubOptions: readonly FilterOption[];
  readonly selectedClubIds: readonly string[];
  readonly onToggleClub: (id: string) => void;
  readonly onClearClubs: () => void;
  readonly isClubFilterOpen: boolean;
  readonly onToggleClubFilter: () => void;
  readonly onCloseClubFilter: () => void;

  /* --- Leagues --- */
  readonly leagueOptions: readonly LeaguePresetOption[];
  readonly selectedLeagueIds: readonly string[];
  readonly onToggleLeague: (id: string) => void;
  readonly onClearLeagues: () => void;
  readonly isLeagueFilterOpen: boolean;
  readonly onToggleLeagueFilter: () => void;
  readonly onCloseLeagueFilter: () => void;
  readonly onChangeLeaguePreset: (ids: readonly string[]) => void;

  /* --- Cup competitions --- */
  readonly competitionOptions: readonly FilterOption[];
  /** True until every competition taxonomy page has been loaded. */
  readonly competitionOptionsLoading?: boolean;
  readonly selectedCompetitionIds: readonly string[];
  readonly onToggleCompetition: (id: string) => void;
  readonly onClearCompetitions: () => void;
  readonly isCompetitionFilterOpen: boolean;
  readonly onToggleCompetitionFilter: () => void;
  readonly onCloseCompetitionFilter: () => void;

  /* --- Server-side search --- */
  /** Server-side search callback for clubs — debounced inside FilterChip */
  readonly onServerSearchClub?: (text: string) => void;
  /** Whether club search is currently loading */
  readonly clubSearching?: boolean;

  /* --- Nationalities --- */
  readonly nationalityOptions: readonly FilterOption[];
  readonly selectedNationalityIds: readonly string[];
  readonly onToggleNationality: (id: string) => void;
  readonly onClearNationalities: () => void;
  readonly isNationalityFilterOpen: boolean;
  readonly onToggleNationalityFilter: () => void;
  readonly onCloseNationalityFilter: () => void;
}

/**
 * Renders the Basic section content: Roles, Positions, Clubs, Leagues, Nationalities, Age
 * @param props - Subset of PlayerFilterSectionsView props for the Basic section
 */
const renderBasicContent = (props: PlayerFilterSectionsViewProps) => (
  <Stack className="gap-4 overflow-visible px-6 py-4">
    <Stack className="gap-1">
      <Text variant="unstyled" className="text-title-sm text-content-muted">
        Roles
      </Text>
      <RoleChipGrid
        options={props.roleOptions}
        selectedIds={props.selectedRoleArchetypes}
        onToggleOption={props.onToggleRole}
      />
    </Stack>

    <Stack className="gap-1">
      <Text variant="unstyled" className="text-title-sm text-content-muted">
        Positions
      </Text>
      <FilterChip
        label="Position"
        options={props.positionOptions}
        selectedIds={props.selectedPositionIds}
        onToggleOption={props.onTogglePosition}
        onClearAll={props.onClearPositions}
        isOpen={props.isPositionFilterOpen}
        onToggle={props.onTogglePositionFilter}
        onClose={props.onClosePositionFilter}
      />
    </Stack>

    <Stack className="gap-1">
      <Text variant="unstyled" className="text-title-sm text-content-muted">
        Clubs
      </Text>
      <FilterChip
        label="Club"
        options={props.clubOptions}
        selectedIds={props.selectedClubIds}
        onToggleOption={props.onToggleClub}
        onClearAll={props.onClearClubs}
        isOpen={props.isClubFilterOpen}
        onToggle={props.onToggleClubFilter}
        onClose={props.onCloseClubFilter}
        onServerSearch={props.onServerSearchClub}
        searching={props.clubSearching}
      />
    </Stack>

    <Stack className="gap-1">
      <Text variant="unstyled" className="text-title-sm text-content-muted">
        Leagues
      </Text>
      <FilterChip
        label="League"
        options={props.leagueOptions}
        selectedIds={props.selectedLeagueIds}
        onToggleOption={props.onToggleLeague}
        onClearAll={props.onClearLeagues}
        isOpen={props.isLeagueFilterOpen}
        onToggle={props.onToggleLeagueFilter}
        onClose={props.onCloseLeagueFilter}
      />
      <LeaguePresetControls
        leagueOptions={props.leagueOptions}
        selectedLeagueIds={props.selectedLeagueIds}
        onChange={props.onChangeLeaguePreset}
      />
    </Stack>

    <Stack className="gap-1">
      <Text variant="unstyled" className="text-title-sm text-content-muted">
        Cup competitions
      </Text>
      <FilterChip
        label="Cup competitions"
        options={props.competitionOptions}
        loading={props.competitionOptionsLoading}
        selectedIds={props.selectedCompetitionIds}
        onToggleOption={props.onToggleCompetition}
        onClearAll={props.onClearCompetitions}
        isOpen={props.isCompetitionFilterOpen}
        onToggle={props.onToggleCompetitionFilter}
        onClose={props.onCloseCompetitionFilter}
      />
    </Stack>

    <Stack className="gap-1">
      <Text variant="unstyled" className="text-title-sm text-content-muted">
        Nationalities
      </Text>
      <FilterChip
        label="Nationality"
        options={props.nationalityOptions}
        selectedIds={props.selectedNationalityIds}
        onToggleOption={props.onToggleNationality}
        onClearAll={props.onClearNationalities}
        isOpen={props.isNationalityFilterOpen}
        onToggle={props.onToggleNationalityFilter}
        onClose={props.onCloseNationalityFilter}
      />
    </Stack>

    <RangeFilterField
      key="age"
      label="Age"
      value={props.ageRange}
      minValue={DEFAULT_AGE_RANGE[0]}
      maxValue={DEFAULT_AGE_RANGE[1]}
      step={1}
      onChange={props.onSetAgeRange}
    />

    <RangeFilterField
      key="months"
      label="Months Remaining"
      value={props.contractMonthsRange}
      minValue={DEFAULT_CONTRACT_MONTHS_RANGE[0]}
      maxValue={DEFAULT_CONTRACT_MONTHS_RANGE[1]}
      step={1}
      onChange={props.onSetContractMonthsRange}
    />
  </Stack>
);

/**
 * Renders the Financial section content: Contract Months, Cost
 * @param props - Subset of PlayerFilterSectionsView props for the Financial section
 */
const renderFinancialContent = (props: PlayerFilterSectionsViewProps) => (
  <Stack className="gap-4 px-6 py-4">
    <RangeFilterField
      key="cost"
      label="Cost (in €)"
      value={props.valuationRange}
      minValue={DEFAULT_VALUATION_RANGE[0]}
      maxValue={DEFAULT_VALUATION_RANGE[1]}
      step={100_000}
      onChange={props.onSetValuationRange}
      isCurrency
      useLogScale
      formatDisplayValue={formatNumberWithCommas}
    />
  </Stack>
);

/** One score-range control: everything that differs between the ten. */
interface ScoreRangeSpec {
  /** React key, preserved from the pre-collapse markup */
  readonly fieldKey: string;
  /** Visible label */
  readonly label: string;
  /** Current [min, max] */
  readonly value: [number, number];
  /** This control's own change handler */
  readonly onChange: (range: [number, number]) => void;
}

/**
 * Renders one section's score-range controls.
 *
 * All ten span DEFAULT_SCORE_RANGE in steps of 1 and differ only in label,
 * value and handler, so the shared shape lives here once rather than ten times.
 * @param specs - The controls for this section, in render order
 */
const renderScoreRanges = (specs: readonly ScoreRangeSpec[]) => (
  <Stack className="gap-4 px-6 py-4">
    {specs.map(spec => (
      <RangeFilterField
        key={spec.fieldKey}
        label={spec.label}
        value={spec.value}
        minValue={DEFAULT_SCORE_RANGE[0]}
        maxValue={DEFAULT_SCORE_RANGE[1]}
        step={1}
        onChange={spec.onChange}
      />
    ))}
  </Stack>
);

/**
 * Renders the Fit & Ratings section content: GPR, Player Style Fit, Team Style Fit
 * @param props - Subset of PlayerFilterSectionsView props for the Fit & Ratings section
 */
const renderFitAndRatingsContent = (props: PlayerFilterSectionsViewProps) =>
  renderScoreRanges([
    {
      fieldKey: "gpr",
      label: "GPR",
      value: props.gprRange,
      onChange: props.onSetGprRange,
    },
    {
      fieldKey: "fit",
      label: "Player Style Fit",
      value: props.fitScoreRange,
      onChange: props.onSetFitScoreRange,
    },
    {
      fieldKey: "teamfit",
      label: "Team Style Fit",
      value: props.teamStyleFitRange,
      onChange: props.onSetTeamStyleFitRange,
    },
  ]);

/**
 * Renders the Performance section content: Carrying, Dribbling, Passing, Shooting
 * @param props - Subset of PlayerFilterSectionsView props for the Performance section
 */
const renderPerformanceContent = (props: PlayerFilterSectionsViewProps) =>
  renderScoreRanges([
    {
      fieldKey: "carrying",
      label: "Carrying",
      value: props.carryingRange,
      onChange: props.onSetCarryingRange,
    },
    {
      fieldKey: "dribbling",
      label: "Dribbling",
      value: props.dribblingRange,
      onChange: props.onSetDribblingRange,
    },
    {
      fieldKey: "passing",
      label: "Passing",
      value: props.passingRange,
      onChange: props.onSetPassingRange,
    },
    {
      fieldKey: "shooting",
      label: "Shooting",
      value: props.shootingRange,
      onChange: props.onSetShootingRange,
    },
  ]);

/**
 * Renders the Goalkeeper section content: GK Passing, Shot Stopping, Positioning
 * @param props - Subset of PlayerFilterSectionsView props for the Goalkeeper section
 */
const renderGoalkeeperContent = (props: PlayerFilterSectionsViewProps) =>
  renderScoreRanges([
    {
      fieldKey: "gkpass",
      label: "GK Passing",
      value: props.gkPassingRange,
      onChange: props.onSetGkPassingRange,
    },
    {
      fieldKey: "gkstop",
      label: "Shot Stopping",
      value: props.gkShotStoppingRange,
      onChange: props.onSetGkShotStoppingRange,
    },
    {
      fieldKey: "gkpos",
      label: "Positioning",
      value: props.gkPositioningRange,
      onChange: props.onSetGkPositioningRange,
    },
  ]);

/**
 * Renders the six collapsible filter sections — every control, no chrome.
 * @param props - All filter section props
 */
const PlayerFilterSectionsView = (props: PlayerFilterSectionsViewProps) => (
  <>
    {/* BASIC section */}
    <Stack className="overflow-visible border-b border-outline-default">
      <SectionHeader
        title="BASIC"
        activeCount={props.sectionCounts.basic}
        isExpanded={props.expandedSections.basic}
        onToggle={props.sectionToggleHandlers.basic}
      />
      {props.expandedSections.basic ? renderBasicContent(props) : null}
    </Stack>

    {/* FINANCIAL section */}
    <Stack className="border-b border-outline-default">
      <SectionHeader
        title="FINANCIAL"
        activeCount={props.sectionCounts.financial}
        isExpanded={props.expandedSections.financial}
        onToggle={props.sectionToggleHandlers.financial}
      />
      {props.expandedSections.financial ? renderFinancialContent(props) : null}
    </Stack>

    {/* FIT & RATINGS section */}
    <Stack className="border-b border-outline-default">
      <SectionHeader
        title="FIT & RATINGS"
        activeCount={props.sectionCounts.fitAndRatings}
        isExpanded={props.expandedSections.fitAndRatings}
        onToggle={props.sectionToggleHandlers.fitAndRatings}
      />
      {props.expandedSections.fitAndRatings
        ? renderFitAndRatingsContent(props)
        : null}
    </Stack>

    {/* PERFORMANCE section */}
    <Stack className="border-b border-outline-default">
      <SectionHeader
        title="PERFORMANCE"
        activeCount={props.sectionCounts.performance}
        isExpanded={props.expandedSections.performance}
        onToggle={props.sectionToggleHandlers.performance}
      />
      {props.expandedSections.performance
        ? renderPerformanceContent(props)
        : null}
    </Stack>

    {/* GOALKEEPER section */}
    <Stack className="border-b border-outline-default">
      <SectionHeader
        title="GOALKEEPER"
        activeCount={props.sectionCounts.goalkeeper}
        isExpanded={props.expandedSections.goalkeeper}
        onToggle={props.sectionToggleHandlers.goalkeeper}
      />
      {props.expandedSections.goalkeeper
        ? renderGoalkeeperContent(props)
        : null}
    </Stack>

    {/* ---- REPORT STATS section ---- */}
    {props.customMetrics.length > 0 || props.sectionCounts.reportStats > 0 ? (
      <Stack className="border-b border-outline-default">
        <SectionHeader
          title="REPORT STATS"
          isExpanded={props.expandedSections.reportStats}
          onToggle={props.sectionToggleHandlers.reportStats}
          activeCount={props.sectionCounts.reportStats}
        />
        {props.expandedSections.reportStats ? (
          <Stack className="gap-4 px-6 pb-4">
            <CustomMetricFilterFields
              customMetrics={props.customMetrics}
              customStatRanges={props.customStatRanges}
              onSetCustomStatRange={props.onSetCustomStatRange}
            />
          </Stack>
        ) : null}
      </Stack>
    ) : null}
  </>
);

PlayerFilterSectionsView.displayName = "PlayerFilterSectionsView";

export type { PlayerFilterSectionsViewProps, SectionKey };

export default memo(PlayerFilterSectionsView);
